/**
 * Logger utility for B2M Tracker
 * Provides consistent logging interface with debug mode control
 */
export class Logger {
  constructor() {
    this.debugMode = false;
    this.prefix = '[B2M Tracker]';
    this.errorPrefix = '[B2M Tracker Error]';
    this.warnPrefix = '[B2M Tracker Warning]';
    this.infoPrefix = '[B2M Tracker Info]';
  }
  
  /**
   * Set debug mode
   * @param {boolean} isDebug - Whether debug mode is enabled
   */
  setDebug(isDebug) {
    this.debugMode = !!isDebug;
  }
  
  /**
   * Log a message (only in debug mode)
   * @param {...any} args - Arguments to log
   */
  log(...args) {
    if (this.debugMode) {
      // Convert objects to JSON for better readability
      const formattedArgs = args.map(arg => {
        if (typeof arg === 'object' && arg !== null) {
          try {
            return JSON.stringify(arg, null, 2);
          } catch (e) {
            return '[Object - circular reference]';
          }
        }
        return arg;
      });
      console.log(this.prefix, ...formattedArgs);
    }
  }
  
  /**
   * Log an error message (always logged)
   * @param {...any} args - Arguments to log
   */
  error(...args) {
    console.error(this.errorPrefix, ...args);
  }
  
  /**
   * Log a warning message (only in debug mode)
   * @param {...any} args - Arguments to log
   */
  warn(...args) {
    if (this.debugMode) {
      console.warn(this.warnPrefix, ...args);
    }
  }
  
  /**
   * Log an info message (only in debug mode)
   * @param {...any} args - Arguments to log
   */
  info(...args) {
    if (this.debugMode) {
      console.info(this.infoPrefix, ...args);
    }
  }
  
  /**
   * Log performance metrics (only in debug mode)
   * @param {string} label - Performance measurement label
   * @param {function} callback - Function to measure
   * @returns {any} - The result of the callback function
   */
  measure(label, callback) {
    if (!this.debugMode) {
      return callback();
    }
    
    console.time(`${this.prefix} ${label}`);
    const result = callback();
    console.timeEnd(`${this.prefix} ${label}`);
    return result;
  }
  
  /**
   * Group logs (only in debug mode)
   * @param {string} label - Group label
   * @param {function} callback - Function to execute within group
   */
  group(label, callback) {
    if (!this.debugMode) {
      return callback();
    }
    
    console.group(`${this.prefix} ${label}`);
    const result = callback();
    console.groupEnd();
    return result;
  }
}

// Create a default instance
const defaultLogger = new Logger();

export default defaultLogger; 